import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../CartContext';
import { formatPrice } from '../currency';

const COLORS = [
  '#2563eb', '#7c3aed', '#db2777', '#d97706',
  '#16a34a', '#0891b2', '#dc2626', '#65a30d',
];

function colorFor(str) {
  if (!str) return COLORS[0];
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffff;
  return COLORS[h % COLORS.length];
}

function cartCurrency(cart) {
  const currencies = [...new Set(cart.map(i => i.currency || 'INR'))];
  return currencies.length === 1 ? currencies[0] : 'INR';
}

export default function Cart() {
  const { cart, updateQty, removeFromCart, cartTotal, courierTotal, grandTotal } = useCart();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-state-icon">🛒</div>
          <h3>Your cart is empty</h3>
          <p style={{ marginTop: 6 }}>Add products from our marketplace</p>
          <Link
            to="/"
            className="btn btn-primary"
            style={{ marginTop: 20, display: 'inline-flex' }}
          >
            Start Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h2 className="section-title">My Cart ({cart.length} items)</h2>

      <div
        className="mk-sidebar-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr minmax(260px, 320px)',
          gap: 20,
          alignItems: 'start',
        }}
      >
        {/* Cart items */}
        <div className="card" style={{ padding: '8px 16px' }}>
          {cart.map((item) => (
            <div key={item.productId} className="cart-item">
              {/* Image / color placeholder */}
              <div
                className="cart-item-img"
                style={{ background: colorFor(item.name) }}
              >
                {(item.name || 'P').charAt(0).toUpperCase()}
              </div>

              {/* Info */}
              <div className="cart-item-info">
                <div className="cart-item-name">{item.name}</div>
                <div className="cart-item-seller">
                  {item.sellerName}
                  {item.city && (
                    <span
                      className="badge badge-muted"
                      style={{ marginLeft: 6, fontSize: 10 }}
                    >
                      {item.city}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--primary)',
                    fontWeight: 700,
                    marginTop: 2,
                  }}
                >
                  {formatPrice(item.price, item.currency)}/{item.unit}
                </div>
              </div>

              {/* Qty control */}
              <div className="qty-control">
                <button
                  className="qty-btn"
                  onClick={() => updateQty(item.productId, item.quantity - 1)}
                >
                  −
                </button>
                <span className="qty-value">{item.quantity}</span>
                <button
                  className="qty-btn"
                  onClick={() => updateQty(item.productId, item.quantity + 1)}
                >
                  +
                </button>
              </div>

              {/* Subtotal */}
              <div
                style={{
                  minWidth: 70,
                  textAlign: 'right',
                  fontWeight: 700,
                  fontSize: 15,
                }}
              >
                {formatPrice(item.price * item.quantity, item.currency)}
              </div>

              {/* Remove */}
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 18,
                  cursor: 'pointer',
                  color: 'var(--muted)',
                  padding: '4px 6px',
                  borderRadius: 6,
                  lineHeight: 1,
                }}
                onClick={() => removeFromCart(item.productId)}
                title="Remove"
              >
                ×
              </button>
            </div>
          ))}
        </div>

        {/* Order summary */}
        <div className="card" style={{ padding: 20 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 16,
              marginBottom: 16,
              paddingBottom: 12,
              borderBottom: '1px solid var(--border)',
            }}
          >
            Order Summary
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              marginBottom: 16,
            }}
          >
            {cart.map((item) => (
              <div
                key={item.productId}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 13,
                }}
              >
                <span style={{ color: 'var(--muted)' }}>
                  {item.name} × {item.quantity}
                </span>
                <span style={{ fontWeight: 600 }}>
                  {formatPrice(item.price * item.quantity, item.currency)}
                </span>
              </div>
            ))}
          </div>

          <hr className="divider" />

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 13,
              marginBottom: 8,
            }}
          >
            <span style={{ color: 'var(--muted)' }}>Subtotal</span>
            <span style={{ fontWeight: 600 }}>{formatPrice(cartTotal, cartCurrency(cart))}</span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 13,
              marginBottom: courierTotal > 0 ? 6 : 16,
            }}
          >
            <span style={{ color: 'var(--muted)' }}>Courier</span>
            {courierTotal > 0
              ? <span style={{ fontWeight: 600 }}>{formatPrice(courierTotal, cartCurrency(cart))}</span>
              : <span style={{ color: 'var(--success)', fontWeight: 600 }}>Free</span>
            }
          </div>
          {courierTotal > 0 && (() => {
            const sellers = [...new Map(cart.map(i => [i.sellerId, i])).values()];
            const multi = sellers.length > 1;
            return multi ? (
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12, paddingLeft: 4 }}>
                {sellers.map(i => i.courierCharge > 0
                  ? `${i.sellerName}: ${formatPrice(i.courierCharge, cartCurrency(cart))}`
                  : `${i.sellerName}: Free`
                ).join(' · ')}
              </div>
            ) : null;
          })()}

          <hr className="divider" />

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontWeight: 800,
              fontSize: 18,
              marginBottom: 20,
            }}
          >
            <span>Total</span>
            <span>{formatPrice(grandTotal, cartCurrency(cart))}</span>
          </div>

          {/* COD badge */}
          <div
            style={{
              background: '#fff7ed',
              color: 'var(--accent)',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 13,
              fontWeight: 600,
              textAlign: 'center',
              marginBottom: 16,
            }}
          >
            💳 Cash on Delivery
          </div>

          <button
            className="btn btn-primary btn-full btn-lg"
            onClick={() => navigate('/checkout')}
          >
            Proceed to Checkout →
          </button>

          <Link
            to="/"
            style={{
              display: 'block',
              textAlign: 'center',
              marginTop: 12,
              fontSize: 13,
              color: 'var(--muted)',
            }}
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
}
